import { createContext, ReactNode, useMemo } from 'react';

import { GetCourseQuery, GetSmLessonQuery } from 'src/graphql/generated/graphql';
import { GetLearningPathQuery } from 'src/graphql/generated/GetLearningPath';
import { generateMetaTags } from 'src/utils/generateMetaTags';

export interface SetHelmetProps {
  title: string;
  seoTags:
    | GetCourseQuery['smCourseCollection']['items'][0]['seo']
    | GetLearningPathQuery['learningPathCollection']['items'][0]['seo']
    | GetSmLessonQuery['smLessonCollection']['items'][0]['seo']
    | Record<string, never>;
}

const setHelmet = (helmet: SetHelmetProps): void => {
  try {
    const head = document.head;

    head.querySelectorAll('[data-react-helmet]').forEach((tag) => tag.remove());
    const metaTags = generateMetaTags(helmet);
    const doc = new DOMParser().parseFromString(metaTags, 'text/html');

    head.prepend(...Array.from(doc.head.children));
  } catch (error) {
    throw new Error('Error setting helmet: ' + error);
  }
};

interface HelmetContextProps {
  setHelmet: (helmet: SetHelmetProps) => void;
}

export const HelmetContext = createContext<HelmetContextProps>({
  setHelmet: () => {},
});

interface Props {
  children: ReactNode;
  setHelmet?: (helmet: SetHelmetProps) => void;
}

export const HelmetProvider = ({ children, setHelmet: setHelmetProp }: Props) => {
  const helmetFunction = setHelmetProp || setHelmet;
  const memoizedSetHelmet = useMemo(() => ({ setHelmet: helmetFunction }), [helmetFunction]);

  return <HelmetContext.Provider value={memoizedSetHelmet}>{children}</HelmetContext.Provider>;
};
